// ─── Collage Element: Shape ───────────────────────────────────
// Renders basic shapes: line, rectangle, circle.
// Configurable fill, stroke, thickness, opacity, borderRadius.
//
// Pattern fills (rectangle only, MVP — stripes):
//   customStyle.fillStyle    = 'solid' | 'pattern'    (default 'solid')
//   customStyle.patternType  = 'stripes'              (extensible)
//   customStyle.patternDirection = 'vertical' | 'horizontal'
//   customStyle.baseColor    = '#f8f1e8'
//   customStyle.stripeColor  = '#23432f'
//   customStyle.stripeWidth  = 12   (px)
//   customStyle.gapWidth     = 12   (px)
//   customStyle.patternOpacity = 1  (0..1)
//
// CSS-only via repeating-linear-gradient — no asset uploads, no SVG.
// Editor + public use this same module so render parity is automatic.

import React from 'react';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';

// Pure helper — exported for future tests and so non-renderer call sites
// (e.g. a future "copy CSS" feature) can derive the same string.
//
// Two sizing modes for stripes (cs.patternSizing):
//   'fixed' (default, legacy)  → repeating-linear-gradient in PIXELS using
//                                stripeWidth/gapWidth.
//   'fit'                      → repeating-linear-gradient in PERCENTAGES.
//
// Pattern types (cs.patternType):
//   'stripes'  (default)  → linear-gradient. Direction:
//                              'vertical' (90°) | 'horizontal' (0°)
//                              'diagonal' (45°) | 'diagonalReverse' (-45°)
//   'dots'                → radial-gradient tile. cs.dotSize / cs.dotGap.
//   'checker'             → two-layer linear-gradient tile. cs.cellSize.
export function buildPatternBackground(cs = {}) {
  if (cs.fillStyle !== 'pattern') return null;
  const patternType = cs.patternType || 'stripes';
  const baseColor = cs.baseColor || '#ffffff';
  const stripeColor = cs.stripeColor || '#1a1a1a';

  // ── Dots ─────────────────────────────────────────────────────
  if (patternType === 'dots') {
    const sizing = cs.patternSizing === 'fit' ? 'fit' : 'fixed';
    if (sizing === 'fit') {
      // Responsive dots: tile is sized as a percentage of the container so
      // cols × tileWidth = 100%. Dot diameter is expressed as % of tile
      // width (≤80) which guarantees the dot fits fully inside its tile —
      // no half-dot clipping at any edge, on any device. Equivalent for
      // dots of "Start/end stripe" for stripes is keeping the dot inside
      // its tile, which is automatic with this construction.
      const cols = Math.max(2, Math.min(40, Math.round(Number.isFinite(cs.dotColumns) ? cs.dotColumns : 8)));
      // dot diameter relative to the tile (% of tile width). 50 = half tile.
      const ratio = Math.max(10, Math.min(80, Number.isFinite(cs.dotFitSize) ? cs.dotFitSize : 50)) / 100;
      // radial-gradient stops are relative to the *tile* (background-size box),
      // so percentages here are percentages of the tile. Place dot centered;
      // outer stop = 50% × ratio → diameter = ratio × tile width.
      const stopPct = (50 * ratio).toFixed(3);
      const tilePct = 100 / cols;
      return {
        __compound: true,
        backgroundImage: `radial-gradient(circle at center, ${stripeColor} 0 ${stopPct}%, transparent ${(Number(stopPct) + 0.3).toFixed(3)}%)`,
        backgroundSize: `${tilePct}% ${tilePct}%`,
        backgroundColor: baseColor,
      };
    }
    const size = Math.max(2, Math.min(80, Number.isFinite(cs.dotSize) ? cs.dotSize : 6));
    const gap = Math.max(2, Math.min(120, Number.isFinite(cs.dotGap) ? cs.dotGap : 14));
    const tile = size + gap;
    return {
      __compound: true,
      backgroundImage: `radial-gradient(circle at center, ${stripeColor} 0 ${size / 2}px, transparent ${size / 2 + 0.5}px)`,
      backgroundSize: `${tile}px ${tile}px`,
      backgroundColor: baseColor,
    };
  }

  // ── Checker ──────────────────────────────────────────────────
  if (patternType === 'checker') {
    const sizing = cs.patternSizing === 'fit' ? 'fit' : 'fixed';
    if (sizing === 'fit') {
      // Responsive checker via conic-gradient — renders LITERAL checker
      // cells (no diagonal fragments). One conic tile = 2×2 cells. We tile
      // it by setting background-size to (200/cols)% of the rectangle. The
      // tile width = horizontal repeat unit; the tile height tracks tile
      // width via aspect ratio of the background-size box. We use the same
      // percentage for both axes — when the rectangle isn't a perfect
      // multiple of cell-height, vertical edges may not align, which per
      // spec is acceptable (clean horizontal fit is the priority).
      const cols = Math.max(2, Math.min(40, Math.round(Number.isFinite(cs.cellColumns) ? cs.cellColumns : 8)));
      const tilePct = 200 / cols; // 2 cells wide per tile
      const a = stripeColor;
      const b = baseColor;
      return {
        __compound: true,
        backgroundImage:
          `conic-gradient(${a} 25%, ${b} 0 50%, ${a} 0 75%, ${b} 0)`,
        backgroundSize: `${tilePct}% ${tilePct}%`,
        backgroundPosition: '0 0',
        backgroundColor: b,
      };
    }
    const cell = Math.max(2, Math.min(120, Number.isFinite(cs.cellSize) ? cs.cellSize : 16));
    const a = stripeColor;
    const b = baseColor;
    return {
      __compound: true,
      backgroundImage:
        `linear-gradient(45deg, ${a} 25%, transparent 25%, transparent 75%, ${a} 75%), ` +
        `linear-gradient(45deg, ${a} 25%, transparent 25%, transparent 75%, ${a} 75%)`,
      backgroundSize: `${cell * 2}px ${cell * 2}px`,
      backgroundPosition: `0 0, ${cell}px ${cell}px`,
      backgroundColor: b,
    };
  }

  // ── Stripes (default) ────────────────────────────────────────
  if (patternType !== 'stripes') return null;
  // Direction → gradient angle. Diagonals are 45° / -45°.
  const dir = cs.patternDirection || 'vertical';
  const angle =
    dir === 'horizontal' ? 0
      : dir === 'diagonal' ? 45
        : dir === 'diagonalReverse' ? -45
          : 90; // vertical (default)
  const sizing = cs.patternSizing === 'fit' ? 'fit' : 'fixed';

  if (sizing === 'fit') {
    const cycles = Math.max(2, Math.min(80, Math.round(Number.isFinite(cs.stripeCycles) ? cs.stripeCycles : 18)));
    const ratio = Math.max(0.1, Math.min(0.9, Number.isFinite(cs.stripeRatio) ? cs.stripeRatio : 0.5));
    const edgeMode = cs.stripeEdgeMode === 'startEndStripe' ? 'startEndStripe' : 'even';

    if (edgeMode === 'startEndStripe') {
      // Both edges land on stripe color. We render exactly `cycles` stripe
      // bands with `cycles - 1` gap bands between them — a NON-repeating
      // linear-gradient with explicit stops. The cycle math:
      //   denom = cycles * ratio + (cycles - 1) * (1 - ratio)
      //   stripe band width % = 100 * ratio / denom
      //   gap band width %    = 100 * (1 - ratio) / denom
      // Total = cycles * stripe + (cycles - 1) * gap = 100% exactly.
      const denom = cycles * ratio + (cycles - 1) * (1 - ratio);
      const stripePct = (100 * ratio) / denom;
      const gapPct = (100 * (1 - ratio)) / denom;
      let pos = 0;
      const stops = [];
      for (let i = 0; i < cycles; i++) {
        const stripeStart = pos;
        pos += stripePct;
        stops.push(`${stripeColor} ${stripeStart}% ${pos}%`);
        if (i < cycles - 1) {
          const gapStart = pos;
          pos += gapPct;
          stops.push(`${baseColor} ${gapStart}% ${pos}%`);
        }
      }
      return `linear-gradient(${angle}deg, ${stops.join(', ')})`;
    }

    // 'even' (default) — current behavior unchanged.
    const cyclePct = 100 / cycles;
    const stripeEndPct = cyclePct * ratio;
    return `repeating-linear-gradient(${angle}deg, ${stripeColor} 0 ${stripeEndPct}%, ${baseColor} ${stripeEndPct}% ${cyclePct}%)`;
  }

  // Fixed (legacy) mode — unchanged behavior.
  const stripeWidth = Math.max(1, Number.isFinite(cs.stripeWidth) ? cs.stripeWidth : 12);
  const gapWidth = Math.max(0, Number.isFinite(cs.gapWidth) ? cs.gapWidth : 12);
  return `repeating-linear-gradient(${angle}deg, ${stripeColor} 0 ${stripeWidth}px, ${baseColor} ${stripeWidth}px ${stripeWidth + gapWidth}px)`;
}

export default function ShapeElement({ element, isMobile, skipPositioning }) {
  const style = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);
  const cs = element.customStyle || {};

  const stroke = cs.stroke || element.stroke || '#000000';
  const strokeWidth = cs.strokeWidth ?? element.strokeWidth ?? 2;
  const opacity = cs.opacity ?? 1;
  const borderRadius = cs.borderRadius ?? 0;
  // Resolve pattern. `buildPatternBackground` returns either:
  //   • a string (stripes — single backgroundImage), or
  //   • a compound object { __compound, backgroundImage, backgroundSize,
  //     backgroundPosition?, backgroundColor } for tiled patterns (dots,
  //     checker) where multiple CSS background properties must agree.
  const pattern = buildPatternBackground(cs);
  const patternIsString = typeof pattern === 'string';
  const patternIsCompound = pattern && typeof pattern === 'object' && pattern.__compound;
  const fill = patternIsString ? pattern : (cs.fill || element.fill || 'transparent');
  // Pattern offset (stripes only — tiled patterns ignore offset for now,
  // because dots/checker need to align with the tile grid).
  const offsetX = (cs.patternOffsetX ?? 0);
  const offsetY = (cs.patternOffsetY ?? 0);
  let patternStyle = null;
  if (patternIsString) {
    patternStyle = {
      backgroundImage: pattern,
      backgroundPosition: `${offsetX}px ${offsetY}px`,
      backgroundRepeat: 'repeat',
    };
  } else if (patternIsCompound) {
    patternStyle = {
      backgroundImage: pattern.backgroundImage,
      backgroundSize: pattern.backgroundSize,
      backgroundPosition: pattern.backgroundPosition || '0 0',
      backgroundColor: pattern.backgroundColor,
      backgroundRepeat: 'repeat',
    };
  }

  if (element.style === 'horizontal_line') {
    return (
      <div style={style} className="select-none flex items-center">
        <div
          style={{
            width: '100%',
            height: `${Math.max(1, strokeWidth)}px`,
            background: stroke,
            opacity,
            borderRadius: borderRadius ? `${borderRadius}px` : undefined,
          }}
        />
      </div>
    );
  }

  if (element.style === 'rectangle') {
    const aspectRatio = cs.aspectRatio || element.aspectRatio || '3/1';
    return (
      <div style={style} className="select-none">
        <div
          style={{
            width: '100%',
            aspectRatio,
            // Pattern fills use backgroundImage + backgroundPosition so the
            // stripe repetition can be visually shifted via patternOffsetX/Y
            // without changing element layout. Solid fills keep the existing
            // shorthand for byte-identical behavior.
            ...(patternStyle ? patternStyle : { background: fill }),
            border: strokeWidth > 0 ? `${strokeWidth}px solid ${stroke}` : 'none',
            borderRadius: borderRadius ? `${borderRadius}px` : undefined,
            opacity,
          }}
          data-fill-style={cs.fillStyle || 'solid'}
        />
      </div>
    );
  }

  if (element.style === 'circle') {
    return (
      <div style={style} className="select-none">
        <div
          style={{
            width: '100%',
            aspectRatio: '1/1',
            background: fill,
            border: strokeWidth > 0 ? `${strokeWidth}px solid ${stroke}` : 'none',
            borderRadius: '50%',
            opacity,
          }}
          data-fill-style={cs.fillStyle || 'solid'}
        />
      </div>
    );
  }

  return null;
}
